/*
 * The client's pages (draft 3), opened from a text or email with no sign in: the pay page (tips 10, 15, 20 percent or
 * Other with nothing picked to start, the card always saved and the page says so, pay by card through Stripe test mode) and
 * the quote page (pick add-ons, type your name to approve). The brand's color runs across the top and bottom.
 */
import { useNavigate, useRouter } from "@tanstack/react-router";
import {
  cardKeptSentence,
  quoteTotals,
  saveCardSentence,
  type PublicCardPage,
  type PublicInvoice,
  type PublicProposal,
  type PublicQuote,
  type ReviewPagePublic,
  type ReviewRateAnswer,
  type VisitReportPublic,
  byYard,
  yardLabel,
  clientLinkTitle,
} from "@steward/shared";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { api, quoteFileSrc } from "../api/client";
import { useEffect, useRef, useState, type CSSProperties, type ReactNode } from "react";
import {
  approveQuote,
  finishPay,
  paySaved,
  startPay,
  startTip,
  testPay,
  usePublicInvoice,
  usePublicQuote,
  type PayStart,
} from "../api/money";
import { loadStripe, type Stripe, type StripeElements } from "@stripe/stripe-js";
import { moneyExact, shortDate } from "../components/format";
import { Icon } from "../components/Icon";
import { Banner, Button, Skeleton } from "../components/parts";
import { CardBox, CardTermsLink, useCardFields, type CardFields } from "../components/CardFields";
import { SignaturePad } from "../components/SignaturePad";
import { errText } from "../components/work";
import { AutoQuoteBody } from "./AutoQuote";
import { SectionsShown } from "./QuoteSections";

/** "(504) 900-9981" from "+15049009981"; anything else as typed. */
function prettyPhone(p: string): string {
  const d = p.replace(/\D/g, "").replace(/^1(?=\d{10}$)/, "");
  return d.length === 10 ? `(${d.slice(0, 3)}) ${d.slice(3, 6)}-${d.slice(6)}` : p;
}

/**
 * My account (owner, 2026-11-01): a menu button on the right of the band, like a website's, opening the client's hub from
 * this pay or quote link (no code to look) at its home, visits or invoices, plus a call button.
 */
function AccountMenu({ account, phone }: { account: string; phone: string | null }) {
  const [open, setOpen] = useState(false);
  const box = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (!open) return;
    const away = (e: MouseEvent) => {
      if (!box.current?.contains(e.target as Node)) setOpen(false);
    };
    const key = (e: KeyboardEvent) => {
      if (e.key === "Escape") setOpen(false);
    };
    document.addEventListener("mousedown", away);
    window.addEventListener("keydown", key);
    return () => {
      document.removeEventListener("mousedown", away);
      window.removeEventListener("keydown", key);
    };
  }, [open]);
  return (
    <div className="public-menu" ref={box}>
      <button type="button" className="public-menu-btn" aria-label="Menu" aria-expanded={open} onClick={() => setOpen((o) => !o)}>
        <Icon name={open ? "close" : "menu"} size={24} />
      </button>
      {open ? (
        <nav className="public-menu-list" aria-label="My account">
          <a href={account}>
            <Icon name="person" size={20} />
            My account
          </a>
          <a href={`${account}#visits`}>
            <Icon name="schedule" size={20} />
            My visits
          </a>
          <a href={`${account}#invoices`}>
            <Icon name="invoices" size={20} />
            My invoices
          </a>
          {phone ? (
            <a href={`tel:${phone}`}>
              <Icon name="phone" size={20} />
              Call us
            </a>
          ) : null}
        </nav>
      ) : null}
    </div>
  );
}

/**
 * The old Steward's client frame (clientframe.php): the brand's white logo on its accent color on top, nothing to tap away
 * to, and the footer band in its footer color with its name, address, phone, links and the year. Always light.
 */
function Branded({
  brand,
  children,
  wide,
  account,
  title,
}: {
  brand: PublicInvoice["brand"] | null;
  children: ReactNode;
  wide?: boolean;
  /** My account (owner, 2026-11-01): the client's hub, opened from this pay or quote link with no code. */
  account?: string | undefined;
  /** The browser tab's title (link-titles.ts); the brand's name without one, never "Steward" (owner, 2026-11-02). */
  title?: string | undefined;
}) {
  const tab = title ?? brand?.name;
  useEffect(() => {
    if (!tab) return;
    const was = document.title;
    document.title = tab;
    return () => {
      document.title = was;
    };
  }, [tab]);
  const look = brand?.look;
  const top = look?.accent ?? brand?.color ?? "#1f6f5c";
  const bottom = look?.menu ?? "#1d1d1f";
  const foot = useRef<HTMLElement>(null);
  // On a phone the brand runs into the bars (owner, 2026-11-01): the status bar and pulling past the top take the top band's
  // color, pulling past the end takes the footer's. iPhone Safari 26 skips theme-color and tints from the page color (html),
  // which is also what shows past either end, so html is the band's color until the footer is on screen, then the footer's.
  useEffect(() => {
    const metas = [...document.querySelectorAll<HTMLMetaElement>('meta[name="theme-color"]')];
    const was = metas.map((m) => m.content);
    for (const m of metas) m.content = top;
    const root = document.documentElement;
    const before = { html: root.style.backgroundColor, body: document.body.style.backgroundColor };
    document.body.style.backgroundColor = bottom;
    const paint = () => {
      const f = foot.current?.getBoundingClientRect();
      root.style.backgroundColor = window.scrollY > 0 && f && f.top < window.innerHeight ? bottom : top;
    };
    paint();
    window.addEventListener("scroll", paint, { passive: true });
    window.addEventListener("resize", paint);
    return () => {
      window.removeEventListener("scroll", paint);
      window.removeEventListener("resize", paint);
      metas.forEach((m, n) => (m.content = was[n] ?? m.content));
      root.style.backgroundColor = before.html;
      document.body.style.backgroundColor = before.body;
    };
  }, [top, bottom]);
  const style = {
    "--brand": top,
    "--brand-deep": look?.deep ?? brand?.color ?? "#1f6f5c",
    "--brand-menu": bottom,
    "--brand-cta": look?.cta ?? brand?.color ?? "#1f6f5c",
    "--brand-on-cta": look?.onCta ?? "#ffffff",
  } as CSSProperties;
  return (
    <div className="public" style={style}>
      <header className="public-top">
        {look?.logo ? <img src={look.logo} alt={brand?.name ?? ""} /> : brand ? <b>{brand.name}</b> : null}
        {account ? <AccountMenu account={account} phone={brand?.phone ?? null} /> : null}
      </header>
      <main className={`public-main${wide ? " wide" : ""}`}>
        {__PREVIEW__ ? <PreviewBack /> : null}
        {children}
      </main>
      {brand ? (
        <footer className="public-foot" ref={foot}>
          <b>{brand.name}</b>
          {brand.address ? <> · {brand.address}</> : null}
          {brand.phone ? (
            <>
              {" "}
              · <a href={`tel:${brand.phone.replace(/[^0-9+]/g, "")}`}>{prettyPhone(brand.phone)}</a>
            </>
          ) : null}
          {brand.links.length ? (
            <>
              <br />
              {brand.links.map((l, n) => (
                <span key={l.label}>
                  {n ? " · " : null}
                  <a href={l.url} target="_blank" rel="noopener noreferrer">
                    {l.label}
                  </a>
                </span>
              ))}
            </>
          ) : null}
          <br />
          <CardTermsLink /> · © {new Date().getFullYear()} {brand.name}
        </footer>
      ) : (
        <footer className="public-foot" />
      )}
    </div>
  );
}

/** The preview has no address bar, so the client pages get a way back to the app. */
function PreviewBack() {
  const router = useRouter();
  return (
    <button type="button" className="link small" onClick={() => router.history.back()}>
      Back to the app (preview only)
    </button>
  );
}

/** A card brand as people say it (Stripe sends visa, mastercard, amex). */
function brandWord(brand: string): string {
  const b = brand.toLowerCase();
  return b === "amex" ? "Amex" : b === "mastercard" ? "Mastercard" : b.charAt(0).toUpperCase() + b.slice(1);
}

/** The card's own badge, the same ones as Cards we take; a brand we have none for shows nothing. */
function CardLogo({ brand }: { brand: string }) {
  const b = brand.toLowerCase();
  if (b === "visa")
    return (
      <span className="cp-cb visa" aria-hidden="true">
        VISA
      </span>
    );
  if (b === "amex")
    return (
      <span className="cp-cb amex" aria-hidden="true">
        AMEX
      </span>
    );
  if (b === "discover")
    return (
      <span className="cp-cb dsc" aria-hidden="true">
        DISCOVER
      </span>
    );
  if (b === "mastercard")
    return (
      <span className="cp-cb mc" aria-hidden="true">
        <i />
        <i />
      </span>
    );
  return null;
}

function NotFound({ what }: { what: string }) {
  return (
    <Branded brand={null}>
      <h1>This link does not work</h1>
      <p className="muted">The {what} may have been replaced. Ask us for a new link.</p>
    </Branded>
  );
}

// Pay page -------------------------------------------------------------------------------------------------------------

/** Stripe's names for the ways a page can take (the server makes the payment with the same ones). */
const STRIPE_WAY = { card: "card", bank: "us_bank_account", klarna: "klarna" } as const;
/** The pay page's way buttons, the old Steward's words and icons (invpay.php). */
const WAY_WORDS = { card: "Credit / Debit", bank: "Bank payment", klarna: "Klarna" } as const;
const WAY_ICON = {
  card: (
    <svg viewBox="0 0 24 24" width="20" height="20" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
      <rect x="2.5" y="5" width="19" height="14" rx="2" />
      <path d="M2.5 10h19" />
    </svg>
  ),
  bank: (
    <svg
      viewBox="0 0 24 24"
      width="20"
      height="20"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <path d="M3 9l9-5 9 5" />
      <path d="M5 9v9M9.5 9v9M14.5 9v9M19 9v9M3 20h18" />
    </svg>
  ),
  klarna: (
    <svg viewBox="0 0 24 24" width="20" height="20" aria-hidden="true">
      <rect x="2" y="4" width="20" height="16" rx="4" fill="#ffb3c7" />
      <path d="M8 8v8M14 8c0 2.5-1.5 4-3.5 4L15 16" fill="none" stroke="#0b051d" strokeWidth="2" strokeLinecap="round" />
    </svg>
  ),
} as const;

const LOCK = (
  <svg
    width="18"
    height="18"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2.2"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
  >
    <rect x="4" y="11" width="16" height="10" rx="2" />
    <path d="M8 11V7a4 4 0 0 1 8 0v4" />
  </svg>
);

/** "Oct 30, 2026" from a day or a time. */
const longDay = (iso: string) =>
  new Date(/^\d{4}-\d{2}-\d{2}$/.test(iso) ? `${iso}T12:00:00` : iso).toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
  });

/**
 * The pay page, laid out like the old Steward's (invpay.php; the owner's shots, 2026-10-30): their name and the brand on top,
 * the invoice folded under View invoice (as Jobber's client hub), the amount large, the optional tip, then Payment method
 * with Stripe's card fields open from the start, Confirm and Pay in the brand's button color, the one-time charge words,
 * the cards taken, and the three trust points.
 */
export function PayPage({ token, session }: { token: string; session?: string }) {
  // session: the payment_intent Stripe adds to the address when it comes back here after a bank check (3D Secure).
  const inv = usePublicInvoice(token);
  const qc = useQueryClient();
  const [stripe, setStripe] = useState<{ s: Stripe; e: StripeElements } | null>(null);
  const cardBox = useRef<HTMLDivElement>(null);
  const [tip, setTip] = useState<number | "other" | null>(null);
  const [other, setOther] = useState("");
  const [name, setName] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const finished = useRef(false);
  // A paid invoice shows the check and thanks; the tip box and card fields open only when they press Add a tip.
  const [tipOpen, setTipOpen] = useState(false);
  // The payment went through: the form folds away before the paid check comes in.
  const [closing, setClosing] = useState(false);
  const i = inv.data;

  // Back from Stripe: tell the server once. The webhook may have marked it paid already; paying stays one time either way.
  useEffect(() => {
    if (!session || finished.current) return;
    finished.current = true;
    void finishPay(token, session)
      .then(() => qc.invalidateQueries({ queryKey: ["pay", token] }))
      .catch((e: unknown) => setErr(errText(e, "We could not check the payment. Refresh in a minute.")));
  }, [session, token, qc]);

  const canTip = !!i && i.status === "paid" && i.tipUntil !== null && i.tipChoices.length > 0;
  const tipAfter = canTip && tipOpen;
  const open = !!i && ((i.status === "open" && i.balanceCents > 0 && i.bankOnTheWayCents === 0) || tipAfter);
  const otherCents = Math.round(Number(other.replace(/[$,\s]/g, "")) * 100);
  const tipCents =
    !i || tip === null
      ? 0
      : tip === "other"
        ? Math.max(0, otherCents || 0)
        : (i.tipChoices.find((c) => c.percent === tip)?.cents ?? 0);
  const payCents = (tipAfter ? 0 : (i?.balanceCents ?? 0)) + tipCents;
  const pk = i?.publishableKey ?? null;
  const ways: readonly ("card" | "bank" | "klarna")[] = tipAfter ? ["card"] : (i?.payWays ?? ["card"]);
  // The way picked with the buttons, like the old Steward's pay page: Credit / Debit, Bank payment, Klarna.
  const [picked, setPicked] = useState<"card" | "bank" | "klarna">("card");
  const kind = ways.includes(picked) ? picked : "card";
  // The card they saved before, picked from the start (owner, 2026-11-01); Use a different card opens Stripe's fields.
  const [newCard, setNewCard] = useState(false);
  const savedCard = i?.cardOnFile && i.canPayCard && !tipAfter && kind === "card" && !newCard ? i.cardOnFile : null;
  // A card payment always keeps the card on file (owner, 2026-11-01); a tip after paying or the saved card does not.
  const save = kind === "card" && !tipAfter && !savedCard;
  const fieldsOn = savedCard === null;
  // Charges after each visit (owner, 2026-11-02): said on the page where paying is the OK (offer), a box to tick where the
  // brand asks first (ask). Only for a card payment, never a tip after paying.
  const [autoTick, setAutoTick] = useState(false);
  const autoBox = kind === "card" && !tipAfter && i?.autoCharge === "ask";
  const autoAfter = kind === "card" && !tipAfter && (i?.autoCharge === "offer" || (autoBox && autoTick));
  const autoOk = autoBox && autoTick;
  const walletBox = useRef<HTMLDivElement>(null);
  const [wallet, setWallet] = useState<{ s: Stripe; e: StripeElements } | null>(null);
  const [hasWallet, setHasWallet] = useState(false);
  // What the Apple Pay or Google Pay button pays with, read when it is pressed (it is made once).
  const live = useRef({ tipCents: 0, save: false, tipAfter: false, autoOk: false });
  const paidNow = async () => {
    setClosing(true);
    await new Promise((r) => setTimeout(r, matchMedia("(prefers-reduced-motion: reduce)").matches ? 0 : 450));
    await qc.invalidateQueries({ queryKey: ["pay", token] });
    setTip(null);
    setTipOpen(false);
    setClosing(false);
  };
  live.current = { tipCents, save, tipAfter, autoOk };

  // Stripe's fields for the way picked, open from the start (the payment is made when they press Confirm and Pay).
  useEffect(() => {
    if (!pk || !open || !fieldsOn || !(tipAfter || i.canPayCard)) return;
    let gone = false;
    let drop: (() => void) | null = null;
    void loadStripe(pk).then((s) => {
      if (!s || gone || !cardBox.current) return;
      const e = s.elements({
        mode: "payment",
        amount: Math.max(50, payCents),
        currency: "usd",
        paymentMethodTypes: [STRIPE_WAY[kind]],
        appearance: {
          theme: "stripe",
          variables: {
            colorPrimary: i.brand.look?.accent ?? "#1f6f5c",
            borderRadius: "8px",
            fontFamily: "Inter, system-ui, sans-serif",
          },
        },
        fonts: [{ cssSrc: "https://fonts.googleapis.com/css2?family=Inter:wght@400;600&display=swap" }],
      });
      // Just the fields, like the old page: Apple Pay and Google Pay have their own button above, and the country is US.
      const el = e.create("payment", {
        layout: "tabs",
        fields: { billingDetails: { name: kind === "card" ? "never" : "auto", address: { country: "never" } } },
        wallets: { applePay: "never", googlePay: "never", link: "never" },
        terms: { card: "never" },
      });
      el.mount(cardBox.current);
      drop = () => el.destroy();
      setStripe({ s, e });
    });
    return () => {
      gone = true;
      drop?.();
      setStripe(null);
    };
    // Made once per way; the amount and saving the card are passed on with update below.
  }, [pk, open, kind, fieldsOn]);

  // Apple Pay and Google Pay above the card fields, shown only on a phone or browser that has one.
  useEffect(() => {
    if (!pk || !open || !fieldsOn || kind !== "card" || !(tipAfter || i.canPayCard)) return;
    let gone = false;
    let drop: (() => void) | null = null;
    void loadStripe(pk).then((s) => {
      if (!s || gone || !walletBox.current) return;
      const e = s.elements({ mode: "payment", amount: Math.max(50, payCents), currency: "usd", paymentMethodTypes: ["card"] });
      const ece = e.create("expressCheckout", {
        buttonHeight: 52,
        paymentMethods: { applePay: "auto", googlePay: "auto", link: "never", paypal: "never", amazonPay: "never" },
      });
      ece.on("ready", (ev) => {
        if (!gone) setHasWallet(Boolean(ev.availablePaymentMethods));
      });
      ece.on("confirm", () => {
        void (async () => {
          const now = live.current;
          setBusy(true);
          setErr(null);
          try {
            const checked = await e.submit();
            if (checked.error) throw new Error(checked.error.message ?? "That did not go through.");
            const made = now.tipAfter
              ? await startTip(token, now.tipCents)
              : await startPay(token, now.tipCents, now.save, "card", now.autoOk);
            const r = await s.confirmPayment({
              elements: e,
              clientSecret: made.clientSecret,
              confirmParams: { return_url: window.location.href },
              redirect: "if_required",
            });
            if (r.error) throw new Error(r.error.message ?? "The payment did not go through.");
            await finishPay(token, made.intentId);
            await paidNow();
          } catch (x) {
            setErr(
              x instanceof Error && !(x instanceof TypeError) ? x.message : errText(x, "That did not go through. Try again."),
            );
          } finally {
            setBusy(false);
          }
        })();
      });
      ece.mount(walletBox.current);
      drop = () => ece.destroy();
      setWallet({ s, e });
    });
    return () => {
      gone = true;
      drop?.();
      setWallet(null);
      setHasWallet(false);
    };
  }, [pk, open, kind, fieldsOn]);
  useEffect(() => {
    const keep = save ? "off_session" : null;
    stripe?.e.update({ amount: Math.max(50, payCents), setupFutureUsage: keep });
    wallet?.e.update({ amount: Math.max(50, payCents), setupFutureUsage: keep });
  }, [stripe, wallet, payCents, save, tipAfter, kind]);

  if (inv.error) return <NotFound what="invoice" />;
  if (!i)
    return (
      <Branded brand={null}>
        <Skeleton rows={6} />
      </Branded>
    );

  const late = i.dueAt !== null && new Date(i.dueAt).getTime() < Date.now();
  const chip =
    i.status === "paid"
      ? { words: "Paid", cls: "ok" }
      : i.bankOnTheWayCents > 0
        ? { words: "Processing", cls: "ok" }
        : i.status === "void"
          ? { words: "Closed", cls: "" }
          : late
            ? { words: "Past due", cls: "late" }
            : { words: "Awaiting payment", cls: "" };
  const cardName = name ?? i.client?.name ?? "";
  const canPay = tipAfter || i.canPayCard;

  const confirm = async () => {
    if (tip === "other" && otherCents > i.maxTipCents) {
      setErr(`A tip can be up to ${moneyExact(i.maxTipCents)}.`);
      return;
    }
    if (tipAfter && tipCents <= 0) {
      setErr("Pick a tip first.");
      return;
    }
    setBusy(true);
    setErr(null);
    if (savedCard) {
      try {
        const r = await paySaved(token, tipCents, i.balanceCents, savedCard.last4, autoOk);
        if (r.paid) await paidNow();
        else setErr(`${r.failure ?? "The card did not go through."} Try a different card.`);
      } catch (e) {
        setErr(e instanceof Error && !(e instanceof TypeError) ? e.message : errText(e, "That did not go through. Try again."));
      } finally {
        setBusy(false);
      }
      return;
    }
    try {
      if (stripe) {
        const checked = await stripe.e.submit();
        if (checked.error) throw new Error(checked.error.message ?? "Check the card details.");
      }
      const made: PayStart = tipAfter ? await startTip(token, tipCents) : await startPay(token, tipCents, save, kind, autoOk);
      if (made.fake || !stripe) {
        await testPay(token, made.intentId);
      } else {
        const r = await stripe.s.confirmPayment({
          elements: stripe.e,
          clientSecret: made.clientSecret,
          confirmParams: {
            return_url: window.location.href,
            // The bank side asks for the name itself; the card side takes it from Name on card.
            payment_method_data: {
              billing_details:
                kind === "card"
                  ? { name: cardName.trim() || (i.client?.name ?? ""), address: { country: "US" } }
                  : { address: { country: "US" } },
            },
          },
          redirect: "if_required",
        });
        if (r.error) throw new Error(r.error.message ?? "The card did not go through.");
        await finishPay(token, made.intentId);
      }
      await paidNow();
    } catch (e) {
      setErr(e instanceof Error && !(e instanceof TypeError) ? e.message : errText(e, "That did not go through. Try again."));
    } finally {
      setBusy(false);
    }
  };

  const website = i.brand.website ? i.brand.website.replace(/\/$/, "") : "";
  return (
    <Branded
      brand={i.brand}
      account={`/my?i=${token}`}
      title={clientLinkTitle("invoice", { brand: i.brand.name, number: i.number }).title}
    >
      <div className="cp">
        {/* Their name on top, so the right client knows it is theirs and anyone sent it by mistake sees it is not. */}
        <p className="cp-hi">
          Hi {i.firstName}, thank you for choosing {i.brand.name}.
        </p>

        <details className="cp-inv">
          <summary>
            <span className="cp-sv">View invoice</span>
            <span className="cp-sh">Hide invoice</span>
          </summary>
          <div className="cp-body">
            <div className="cp-ihead">
              <span className="cp-inum">{i.number ? `Invoice #${String(i.number)}` : "Invoice"}</span>
              <span className={`cp-chip ${chip.cls}`}>{chip.words}</span>
            </div>
            {i.jobName ? <div className="cp-job">{i.jobName}</div> : null}
            {i.home ? <div className="cp-fact">For your home, {i.home}</div> : null}
            {i.client ? <div className="cp-who">{i.client.name}</div> : null}
            {i.client?.address ? <div className="cp-fact">{i.client.address}</div> : null}
            {i.client?.phone ? <div className="cp-fact">{prettyPhone(i.client.phone)}</div> : null}
            <div className="cp-rows">
              {i.poNumber ? (
                <div className="cp-row">
                  <span>Work order / PO #</span>
                  <span>{i.poNumber}</span>
                </div>
              ) : null}
              {i.issuedOn ? (
                <div className="cp-row">
                  <span>Issued</span>
                  <span>{longDay(i.issuedOn)}</span>
                </div>
              ) : null}
              <div className="cp-row">
                <span>Due</span>
                <span>{i.dueAt ? longDay(i.dueAt) : "Upon receipt"}</span>
              </div>
            </div>
            <div className="cp-items">
              {i.lines.map((l, n) => (
                <div className="cp-item" key={n}>
                  <div>
                    <div className="cp-iname">
                      {l.description}
                      {l.quantity !== 1 ? ` x ${String(l.quantity)}` : ""}
                    </div>
                    {l.serviceDate || l.note ? (
                      <div className="cp-idesc">
                        {[l.serviceDate ? longDay(l.serviceDate) : null, l.note].filter(Boolean).join(" · ")}
                      </div>
                    ) : null}
                    <SeeIncluded link={l.link} />
                  </div>
                  <div className="cp-iamt">{moneyExact(l.amountCents)}</div>
                </div>
              ))}
            </div>
            <div className="cp-sum">
              <span>Subtotal</span>
              <span>{moneyExact(i.subtotalCents)}</span>
            </div>
            {i.tipCents ? (
              <div className="cp-sum">
                <span>Tip</span>
                <span>{moneyExact(i.tipCents)}</span>
              </div>
            ) : null}
            <div className="cp-sum big">
              <span>Total</span>
              <span>{moneyExact(i.totalCents)}</span>
            </div>
            {i.paidCents > 0 && i.balanceCents > 0 ? (
              <div className="cp-sum">
                <span>Paid so far</span>
                <span>{moneyExact(-i.paidCents)}</span>
              </div>
            ) : null}
            <div className="cp-sum">
              <span>{i.paidCents > 0 && i.balanceCents > 0 ? "Balance due" : "Invoice balance"}</span>
              <span>{moneyExact(i.status === "paid" ? 0 : Math.max(0, i.balanceCents))}</span>
            </div>
            {i.noteToClient ? <p className="cp-msg">{i.noteToClient}</p> : null}
            <p className="cp-thanks">Thank you for choosing {i.brand.name}.</p>
            <a className="cp-pdf" href={`/v1/public/pay/${token}/pdf`} target="_blank" rel="noopener">
              <svg
                width="16"
                height="16"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2.2"
                strokeLinecap="round"
                strokeLinejoin="round"
                aria-hidden="true"
              >
                <path d="M12 4v11M7 10l5 5 5-5M5 20h14" />
              </svg>
              Download PDF
            </a>
          </div>
        </details>

        {i.status === "paid" ? (
          <div className="cp-paid" role="status">
            <svg className="cp-check" viewBox="0 0 52 52" aria-hidden="true">
              <circle cx="26" cy="26" r="25" />
              <path d="M15 27.5l7 7 15-15" />
            </svg>
            <div className="cp-paidh">Paid in full</div>
            <div className="cp-paidamt">{moneyExact(i.totalCents)}</div>
            <p className="cp-paidty">Thank you for supporting us.</p>
            {i.tipCents > 0 ? <div className="cp-tipdone">Tip for your team {moneyExact(i.tipCents)}. Thank you.</div> : null}
            {canTip ? (
              <button type="button" className="cp-addtip" aria-expanded={tipOpen} onClick={() => setTipOpen(!tipOpen)}>
                Want to add a tip?
                <svg viewBox="0 0 24 24" width="18" height="18" aria-hidden="true">
                  <path d="M6 9l6 6 6-6" />
                </svg>
              </button>
            ) : null}
          </div>
        ) : i.status === "void" ? (
          <Banner type="notice">This invoice was closed. You owe nothing on it.</Banner>
        ) : (
          <>
            <div className="cp-lbl">Payment amount</div>
            <div className="cp-due">{moneyExact(i.balanceCents + tipCents)}</div>
            {tipCents > 0 ? (
              <div className="cp-tipdone">Includes a {moneyExact(tipCents)} tip for your team. Thank you.</div>
            ) : null}
          </>
        )}

        {i.bankOnTheWayCents > 0 ? (
          <Banner type="notice">
            Your bank payment of {moneyExact(i.bankOnTheWayCents)} is on its way. Banks take 3 to 5 business days to clear it.
          </Banner>
        ) : null}

        <div className={`cp-fold${closing ? " shut" : ""}`}>
          <div className={tipAfter ? "cp-drop" : undefined}>
            {open && i.tipChoices.length ? (
              <>
                <div className="cp-tipbox">
                  <div className="cp-tiph">Say thanks with a tip</div>
                  <div className="cp-tips" role="group" aria-label="Tip">
                    {i.tipChoices.map((c) => (
                      <button
                        key={c.percent}
                        type="button"
                        className="cp-tip"
                        aria-pressed={tip === c.percent}
                        title={moneyExact(c.cents)}
                        onClick={() => setTip(tip === c.percent ? null : c.percent)}
                      >
                        {c.percent}%
                      </button>
                    ))}
                    <button
                      type="button"
                      className="cp-tip"
                      aria-pressed={tip === "other"}
                      onClick={() => setTip(tip === "other" ? null : "other")}
                    >
                      Other amount
                    </button>
                  </div>
                  {tip === "other" ? (
                    <div className="cp-tipother">
                      <input
                        inputMode="decimal"
                        aria-label="Tip in dollars"
                        placeholder="Tip in dollars"
                        value={other}
                        onChange={(e) => setOther(e.target.value)}
                      />
                    </div>
                  ) : null}
                </div>
                <p className="cp-tipnote">Tip is based on your invoice subtotal</p>
              </>
            ) : null}

            {open ? (
              canPay ? (
                <>
                  <h2 className="cp-h">{tipAfter ? "Pay your tip" : "Payment method"}</h2>
                  {ways.length > 1 ? (
                    <div className="cp-ways" role="radiogroup" aria-label="Payment method">
                      {ways.map((w) => (
                        <button
                          key={w}
                          type="button"
                          role="radio"
                          aria-checked={kind === w}
                          className={`cp-way${kind === w ? " on" : ""}`}
                          onClick={() => {
                            setPicked(w);
                            setErr(null);
                          }}
                        >
                          {WAY_ICON[w]}
                          <span>{WAY_WORDS[w]}</span>
                        </button>
                      ))}
                    </div>
                  ) : null}
                  <div className="cp-pmbox">
                    {i.cardOnFile && kind === "card" && !tipAfter ? (
                      <div className="cp-cards" role="radiogroup" aria-label="Card">
                        <button
                          type="button"
                          role="radio"
                          aria-checked={!newCard}
                          className="cp-cardpick"
                          onClick={() => {
                            setNewCard(false);
                            setErr(null);
                          }}
                        >
                          <span className="cp-dot" aria-hidden="true" />
                          <span>
                            {brandWord(i.cardOnFile.brand)} ending {i.cardOnFile.last4}
                            <small>Saved card</small>
                          </span>
                          <CardLogo brand={i.cardOnFile.brand} />
                        </button>
                        <button
                          type="button"
                          role="radio"
                          aria-checked={newCard}
                          className="cp-cardpick"
                          onClick={() => {
                            setNewCard(true);
                            setErr(null);
                          }}
                        >
                          <span className="cp-dot" aria-hidden="true" />
                          <span>Use a different card</span>
                        </button>
                      </div>
                    ) : null}
                    {kind === "card" && fieldsOn ? (
                      <>
                        <div ref={walletBox} className={`cp-wallet${hasWallet ? " on" : ""}`} />
                        {hasWallet ? <div className="cp-or">OR</div> : null}
                        <label className="cp-fld">
                          Name on card
                          <input type="text" autoComplete="cc-name" value={cardName} onChange={(e) => setName(e.target.value)} />
                        </label>
                      </>
                    ) : null}
                    {!fieldsOn ? null : pk ? (
                      <div ref={cardBox} className="cp-card" />
                    ) : (
                      <Banner type="notice">Card pay is in test mode here. Pay uses the test card 4242.</Banner>
                    )}
                    {kind === "bank" ? (
                      <p className="cp-panehelp">
                        Log in to your bank to pay from your checking or savings account. A bank payment takes 3 to 5 business
                        days to clear.
                      </p>
                    ) : null}
                    {save ? (
                      <div className="cp-save">
                        <svg className="cp-tick" viewBox="0 0 20 20" aria-hidden="true">
                          <rect width="20" height="20" rx="4" />
                          <path d="M5.5 10.5l3 3 6-6.5" />
                        </svg>
                        <span>{cardKeptSentence(i.brand.name)}</span>
                      </div>
                    ) : null}
                    {autoBox ? (
                      <label className="cp-auto">
                        <input type="checkbox" checked={autoTick} onChange={(e) => setAutoTick(e.target.checked)} />
                        <span>
                          <b>Charge my card after each visit</b>
                          <small>No more invoices to pay. You get a receipt by email. Stop anytime.</small>
                        </span>
                      </label>
                    ) : null}
                  </div>
                  {err ? (
                    <div className="cp-err" role="alert">
                      {err}
                    </div>
                  ) : null}
                  <button
                    type="button"
                    className="cp-pay"
                    disabled={busy || (tipAfter && tipCents <= 0)}
                    onClick={() => void confirm()}
                  >
                    {LOCK}
                    <span>
                      {busy
                        ? "Paying..."
                        : tipAfter
                          ? `Add a ${moneyExact(tipCents)} tip`
                          : kind === "klarna"
                            ? "Continue to Klarna"
                            : "Confirm and Pay"}
                    </span>
                  </button>
                  <p className="cp-legal">
                    By selecting {tipAfter ? "Add a tip" : kind === "klarna" ? "Continue to Klarna" : "Confirm and Pay"}, you
                    allow {i.brand.name} to {autoAfter ? "charge" : "make a one-time charge of"} {moneyExact(payCents)}{" "}
                    {kind === "bank"
                      ? "to your bank account"
                      : kind === "klarna"
                        ? "through Klarna"
                        : savedCard
                          ? `to your ${brandWord(savedCard.brand)} ending ${savedCard.last4}`
                          : "to your card"}{" "}
                    today
                    {autoAfter
                      ? ". We keep your card on file and charge it after each visit for your repeat service"
                      : save
                        ? ` and to keep your card on file for future services`
                        : ""}
                    . {kind === "card" ? <CardTermsLink /> : null}
                  </p>
                  {kind === "card" ? (
                    <div className="cp-brands" aria-label="Cards we take">
                      <span className="cp-cb visa">VISA</span>
                      <span className="cp-cb mc">
                        <i />
                        <i />
                      </span>
                      <span className="cp-cb amex">AMEX</span>
                      <span className="cp-cb dsc">DISCOVER</span>
                    </div>
                  ) : null}
                  <div className="cp-trust">
                    <div>
                      <svg
                        viewBox="0 0 24 24"
                        width="22"
                        height="22"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        aria-hidden="true"
                      >
                        <rect x="4" y="11" width="16" height="10" rx="2" />
                        <path d="M8 11V7a4 4 0 0 1 8 0v4" />
                      </svg>
                      <span>Payment details are encrypted</span>
                    </div>
                    <div>
                      <svg
                        viewBox="0 0 24 24"
                        width="22"
                        height="22"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        aria-hidden="true"
                      >
                        <path d="M12 3l8 3v6c0 4.5-3.4 8.3-8 9-4.6-.7-8-4.5-8-9V6z" />
                        <path d="M9 12l2 2 4-4" />
                      </svg>
                      <span>Secure payments with Stripe</span>
                    </div>
                    <div>
                      <svg
                        viewBox="0 0 24 24"
                        width="22"
                        height="22"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        aria-hidden="true"
                      >
                        <path d="M6 3h12v18l-3-2-3 2-3-2-3 2z" />
                        <path d="M9 8h6M9 12h6" />
                      </svg>
                      <span>Proof of payment for your records</span>
                    </div>
                  </div>
                  {website ? (
                    <p className="cp-pp">
                      <a href={`${website}/privacy-policy/`} target="_blank" rel="noopener noreferrer">
                        Privacy Policy
                      </a>
                    </p>
                  ) : null}
                </>
              ) : (
                <p className="cp-note">
                  Online payment is not open for this invoice. Call us{i.brand.phone ? ` at ${prettyPhone(i.brand.phone)}` : ""}{" "}
                  to pay.
                </p>
              )
            ) : null}
          </div>
        </div>
        {i.photos.length ? (
          <div className="cp-photos" aria-label="Photos from the visit">
            {byYard(i.photos).map((ph) => {
              const label = yardLabel(ph.room);
              return (
                <figure key={ph.id}>
                  <img src={`/v1/public/pay/${token}/photos/${ph.id}`} alt={label ?? "Photo from the visit"} />
                  {label ? <figcaption>{label}</figcaption> : null}
                </figure>
              );
            })}
          </div>
        ) : null}
        {i.payNote && i.status === "open" ? <p className="cp-note">{i.payNote}</p> : null}
        {i.footer ? <p className="cp-note">{i.footer}</p> : null}
      </div>
    </Branded>
  );
}

/** After the card link: Stripe sends the client here. The webhook saves the card. */
export function CardSavedPage({ saved }: { saved: boolean }) {
  return (
    <Branded brand={null}>
      <h1>{saved ? "Your card is saved" : "Nothing was saved"}</h1>
      <p>{saved ? "Thank you. Nothing was charged. You can close this page." : "You can use the link again any time."}</p>
    </Branded>
  );
}

// Save a card page -----------------------------------------------------------------------------------------------------

/**
 * Our own save a card page (owner, Oct 31): the office's Text them a link to save a card opens this, never Stripe's page.
 * Stripe's card fields sit in it like on the quote page (api billing/card-page.ts).
 */
export function SaveCardPage({ token }: { token: string }) {
  const page = useQuery({
    queryKey: ["public-card", token],
    queryFn: () => api<PublicCardPage>("GET", `public/card/${token}`),
    retry: false,
  });
  const [saved, setSaved] = useState<PublicCardPage | null>(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const card = useCardFields<PublicCardPage>(token, !!page.data && !saved, "public/card");
  if (page.error) return <NotFound what="card link" />;
  const p = saved ?? page.data;
  if (!p)
    return (
      <Branded brand={null}>
        <Skeleton rows={4} />
      </Branded>
    );
  if (saved?.card)
    return (
      <Branded brand={p.brand}>
        <h1>Your card is saved</h1>
        <p>
          Thank you, {p.firstName}. {saved.card.brand} ending in {saved.card.last4} is on file with {p.brand.name}. Nothing was
          charged. You can close this page.
        </p>
      </Branded>
    );
  const save = async () => {
    setBusy(true);
    setErr(null);
    try {
      setSaved(await card.save());
    } catch (e) {
      setErr(e instanceof Error && !(e instanceof TypeError) ? e.message : errText(e, "The card was not saved."));
    } finally {
      setBusy(false);
    }
  };
  return (
    <Branded brand={p.brand}>
      <h1>Save a card, {p.firstName}</h1>
      <p className="muted">
        {p.card
          ? `You have ${p.card.brand} ending in ${p.card.last4} on file. A new card here takes its place.`
          : `Keep a card on file with ${p.brand.name} so paying is easy.`}{" "}
        Nothing is charged now.
      </p>
      <CardBox
        card={card}
        title="Your card"
        sentence={
          <>
            {saveCardSentence(p.brand.name)} <CardTermsLink />
          </>
        }
      />
      {err ? <Banner type="error">{err}</Banner> : null}
      <Button size="large" fullWidth loading={busy} disabled={card.state === "loading"} onClick={() => void save()}>
        Save card
      </Button>
    </Branded>
  );
}

// Quote page -----------------------------------------------------------------------------------------------------------

/**
 * The client's quote page. previewId shows it to the office first (owner, 2026-11-03), drafts too: add-ons tick like the
 * client's, but nothing signs, pays or saves.
 */
export function PublicQuotePage({ token, previewId }: { token: string; previewId?: string }) {
  const quote = usePublicQuote(token, previewId);
  const preview = Boolean(previewId);
  const qc = useQueryClient();
  const navigate = useNavigate();
  const [picked, setPicked] = useState<string[] | null>(null);
  const [name, setName] = useState("");
  const [drawing, setDrawing] = useState<string | null>(null);
  const [autoPay, setAutoPay] = useState(false);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  if (quote.error) return <NotFound what="quote" />;
  const q = quote.data;
  if (!q)
    return (
      <Branded brand={null}>
        <Skeleton rows={6} />
      </Branded>
    );
  const chosen = picked ?? q.lines.filter((l) => l.optional && l.picked).map((l) => l.id);
  // A proposal's line items list what each includes, under the line.
  const included = (line: string) => q.proposal?.included.find((x) => x.line === line)?.items ?? [];
  // An agency plan (draft 13): a proposal with the 3 month agreement and the auto pay box.
  const plan = q.agreement;
  const open = q.status === "sent" || preview;
  // A job the office booked: the client signs (and saves a card when none is on file) to confirm the day (api jobs/confirm.ts).
  const held = q.confirmFor ?? null;
  const sums = quoteTotals(
    q.lines.map((l) => ({ amountCents: l.amountCents, optional: l.optional, picked: open ? chosen.includes(l.id) : l.picked })),
    q.deposit,
  );
  // One tap: the card first when the quote needs one (Stripe checks it), then the signed approval.
  const approve = async (card: CardFields | null) => {
    if (name.trim().length < 2) {
      setErr("Type your full name to sign.");
      return;
    }
    if (q.signRequired && !drawing) {
      setErr("Sign in the box with your finger or mouse.");
      return;
    }
    setBusy(true);
    setErr(null);
    try {
      if (card) qc.setQueryData(["public-quote", token], await card.save());
      const after: PublicQuote = await approveQuote(token, chosen, name.trim(), drawing, autoPay);
      qc.setQueryData(["public-quote", token], after);
    } catch (e) {
      setErr(e instanceof Error && !(e instanceof TypeError) ? e.message : errText(e, "That did not go through. Try again."));
    } finally {
      setBusy(false);
    }
  };
  // An auto quote (a home from the price list, or a lawn): the website's layout, to change it and book a day.
  if (!preview && !q.proposal && !plan && ((open && q.auto) || q.bookedFor))
    return (
      <Branded
        brand={q.brand}
        wide
        account={q.account ? `/my?q=${token}` : undefined}
        title={clientLinkTitle("quote", { brand: q.brand.name, first: q.firstName }).title}
      >
        <AutoQuoteBody token={token} q={q} />
      </Branded>
    );
  return (
    <Branded
      brand={q.brand}
      wide={Boolean(q.proposal)}
      account={q.account ? `/my?q=${token}` : undefined}
      title={
        q.proposal
          ? proposalTabTitle(q.proposal.greeting, q.brand.name)
          : clientLinkTitle("quote", { brand: q.brand.name, first: q.firstName }).title
      }
    >
      {q.proposal ? (
        <ProposalParts p={q.proposal} number={q.number} brand={q.brand.name} totalCents={sums.totalCents} />
      ) : (
        <>
          {held ? (
            <>
              <h1>Hi {q.firstName}, confirm your booking</h1>
              <p>
                <b>
                  {shortDate(held.date)}
                  {held.time ? ` at ${held.time}` : ""}
                </b>
                {q.title ? ` · ${q.title}` : ""}
                {held.often !== "One time" ? `. Then ${held.often.toLowerCase()}.` : ""}
              </p>
              {held.address ? <p className="muted">{held.address}</p> : null}
              {q.home ? <p className="muted">For your home, {q.home}</p> : null}
              <p className="muted">
                {q.hasCard ? "Sign below to confirm. Your card on file stays on file." : "Save a card and sign below to confirm."}
              </p>
            </>
          ) : (
            <>
              <h1>
                Hi {q.firstName}, here's your {plan ? "proposal" : "quote"}
                {q.title ? ` for ${q.title.toLowerCase()}` : ""}
              </h1>
              <p className="muted">
                Quote #{q.number}
                {q.expiresOn && open ? ` · Good until ${shortDate(q.expiresOn)}` : ""}
              </p>
              {q.home ? <p className="muted">For your home, {q.home}</p> : null}
            </>
          )}
        </>
      )}
      <SectionsShown sections={q.sections} files={q.sectionFiles} src={(id) => quoteFileSrc(token, id)} part="intro" />
      {q.message ? <p className="pre-wrap">{q.message}</p> : null}
      {q.signature && plan ? (
        <Banner type="success">
          Signed by {q.signature.name} on {shortDate(q.signature.at.slice(0, 10))}. Thank you. Pay the first month below to get
          started.
        </Banner>
      ) : q.signature && held ? (
        <Banner type="success">
          Confirmed by {q.signature.name} on {shortDate(q.signature.at.slice(0, 10))}. You are on the schedule. See you then.
        </Banner>
      ) : q.signature ? (
        <Banner type="success">
          Approved by {q.signature.name} on {shortDate(q.signature.at.slice(0, 10))}. Thank you. We will reach out to book it.
        </Banner>
      ) : q.status === "expired" ? (
        <Banner type="notice">This quote has expired. Ask us for a new one.</Banner>
      ) : q.status === "declined" ? (
        <Banner type="notice">This quote is closed.</Banner>
      ) : null}
      {q.signature?.drawing ? (
        <img className="sign-shown" src={q.signature.drawing} alt={`Signed by ${q.signature.name}`} />
      ) : null}
      <PriceBox on={Boolean(q.proposal)} monthly={Boolean(q.proposal && !q.proposal.once)}>
        <table className="public-lines">
          <tbody>
            {q.lines
              .filter((l) => !l.optional)
              .map((l) => (
                <tr key={l.id}>
                  <td>
                    {l.description}
                    {l.note ? <span className="muted small"> {l.note}</span> : null}
                    <SeeIncluded link={l.link} />
                    <Included items={included(l.description)} />
                  </td>
                  <td className="num">
                    {moneyExact(l.amountCents)}
                    {l.bill === "monthly" ? " a month" : ""}
                  </td>
                </tr>
              ))}
          </tbody>
        </table>
        {q.lines.some((l) => l.optionGroup) ? (
          <div className="public-addons" role="radiogroup" aria-label="Pick one">
            <p className="public-label">Pick one</p>
            {q.lines
              .filter((l) => l.optionGroup)
              .map((l) => (
                <label key={l.id} className="public-save">
                  <input
                    type="radio"
                    name="option"
                    disabled={!open}
                    checked={open ? chosen.includes(l.id) : l.picked}
                    onChange={() => setPicked([...chosen.filter((x) => !q.lines.some((o) => o.id === x && o.optionGroup)), l.id])}
                  />
                  <span>
                    <b>{l.optionGroup}</b> {l.description}
                  </span>
                  <span className="num">{moneyExact(l.amountCents)}</span>
                </label>
              ))}
          </div>
        ) : null}
        {q.lines.some((l) => l.optional && !l.optionGroup) ? (
          <div className="public-addons">
            <p className="public-label">Add-ons you can pick</p>
            {q.lines
              .filter((l) => l.optional && !l.optionGroup)
              .map((l) => (
                <label key={l.id} className="public-save">
                  <input
                    type="checkbox"
                    disabled={!open}
                    checked={open ? chosen.includes(l.id) : l.picked}
                    onChange={(e) => setPicked(e.target.checked ? [...chosen, l.id] : chosen.filter((x) => x !== l.id))}
                  />
                  <span>
                    {l.description}
                    {q.proposal && l.note ? <span className="muted small"> {l.note}</span> : null}
                    <Included items={included(l.description)} />
                  </span>
                  <span className="num">{moneyExact(l.amountCents)}</span>
                </label>
              ))}
          </div>
        ) : null}
        <table className="public-lines">
          <tfoot>
            <tr>
              <td>{plan ? "Due when you sign" : held && held.often !== "One time" ? "First visit" : "Total"}</td>
              <td className="num">{moneyExact(plan ? plan.monthlyCents + plan.setupCents : sums.totalCents)}</td>
            </tr>
            {plan ? (
              <tr>
                <td>Then each month</td>
                <td className="num">{moneyExact(plan.monthlyCents)}</td>
              </tr>
            ) : null}
            {sums.depositCents ? (
              <tr>
                <td>Deposit when you approve</td>
                <td className="num">{moneyExact(sums.depositCents)}</td>
              </tr>
            ) : null}
          </tfoot>
        </table>
      </PriceBox>
      <SectionsShown sections={q.sections} files={q.sectionFiles} src={(id) => quoteFileSrc(token, id)} part="rest" />
      {q.proposal?.terms.length ? (
        <section className="pp-section">
          <h2>Terms</h2>
          <div className="pp-terms-box" tabIndex={0} aria-label="Terms">
            <ol className="pp-terms">
              {q.proposal.terms.map((x) => (
                <li key={x}>{x}</li>
              ))}
            </ol>
          </div>
          <p className="muted small">Signing below accepts this proposal and these terms as our service agreement.</p>
        </section>
      ) : q.terms ? (
        <details className="public-terms" open={open}>
          <summary>Service agreement</summary>
          <p>{q.terms}</p>
        </details>
      ) : null}
      {plan ? (
        <div className="public-terms">
          <p className="public-label">How the plan works</p>
          <ul>
            {plan.words.map((w) => (
              <li key={w}>{w}</li>
            ))}
          </ul>
        </div>
      ) : null}
      {plan && open ? (
        <label className="public-save">
          <input type="checkbox" checked={autoPay} onChange={(e) => setAutoPay(e.target.checked)} />
          <span>{plan.autoPayWords}</span>
        </label>
      ) : null}
      {preview ? (
        <Banner type="notice">
          Preview. This is what the client sees{q.status === "draft" ? " once you send it" : ""}. Signing is off here.
        </Banner>
      ) : null}
      {open && !preview ? (
        <ApproveBox
          token={token}
          q={q}
          needCard={q.cardRequired && !q.hasCard}
          name={name}
          setName={setName}
          setDrawing={setDrawing}
          err={err}
          busy={busy}
          go={(card) => void approve(card)}
          nameWords={
            q.proposal
              ? "Type your full name to sign"
              : held
                ? "Type your full name to confirm"
                : "Type your full name to approve"
          }
          goWords={plan || q.proposal ? "Sign the proposal" : held ? "Confirm my booking" : "Approve quote"}
          agreeWords={
            q.proposal?.terms.length ? "I agree to the terms above" : q.terms ? "I agree to the service agreement above" : ""
          }
        />
      ) : null}
      {q.payLink && !preview ? (
        <Button
          size="large"
          fullWidth
          onClick={() => void navigate({ to: "/pay/$token", params: { token: q.payLink?.split("/").pop() ?? "" }, search: {} })}
        >
          {plan
            ? `Pay ${moneyExact(plan.monthlyCents + plan.setupCents)} now`
            : `Pay the ${moneyExact(sums.depositCents)} deposit`}
        </Button>
      ) : null}
      {q.proposal ? (
        <Button type="secondary" onClick={() => window.print()}>
          Print or save as PDF
        </Button>
      ) : null}
    </Branded>
  );
}

/** A proposal's tab title: the person's first name when the greeting has one, never the first word of the business. */
function proposalTabTitle(greeting: string, brand: string): string {
  const first = greeting
    .replace(/^Hello\s*/, "")
    .replace(/,$/, "")
    .trim();
  return `${first ? `${first}, your` : "Your"} proposal from ${brand}`;
}

/** The price lines in their own card on a proposal (owner, 2026-11-03), as they were on a plain quote. */
function PriceBox({ on, monthly, children }: { on: boolean; monthly: boolean; children: ReactNode }) {
  if (!on) return <>{children}</>;
  return (
    <section className="pp-section pp-pricebox">
      <h2>{monthly ? "Your monthly price" : "Your price"}</h2>
      {children}
    </section>
  );
}

/**
 * A commercial proposal above the price (the old Steward's steward_proposal_page_html), in the owner's own layout: the
 * cover, a short letter, at a glance, their building, the scope as a checklist, what we bring, insurance, why us, how we
 * keep it right, and what clients say. The page prints as a clean PDF.
 */
function ProposalParts({
  p,
  number,
  brand,
  totalCents,
}: {
  p: PublicProposal;
  number: number;
  brand: string;
  totalCents: number;
}) {
  const check = (items: string[]) => (
    <ul className="pp-check">
      {items.map((x) => (
        <li key={x}>{x}</li>
      ))}
    </ul>
  );
  return (
    <>
      <section className="pp-cover">
        <div className="pp-kicker">{p.serviceWords} proposal</div>
        <h1>{p.building}</h1>
        <div className="pp-meta">
          <span>
            <b>Prepared for</b>
            {p.preparedFor}
            {p.address ? <br /> : null}
            {p.address}
          </span>
          <span>
            <b>Prepared by</b>
            {brand}
            <br />
            {p.date}
          </span>
          <span>
            <b>Proposal</b>#{number}
          </span>
        </div>
      </section>
      {p.letter.length ? (
        <section className="pp-section">
          <p>{p.greeting}</p>
          {p.letter.map((x) => (
            <p key={x}>{x}</p>
          ))}
          <p>
            Best,
            <br />
            <b>{p.signName}</b>
            {p.signTitle ? (
              <>
                <br />
                {p.signTitle}
              </>
            ) : null}
          </p>
        </section>
      ) : null}
      <div className="pp-glance">
        <div>
          <span>Service</span>
          <b>{p.serviceWords}</b>
        </div>
        <div>
          <span>{p.serviceWords === "Construction cleanup" ? "When" : "Schedule"}</span>
          {p.whenLines.length ? (
            <ul className="pp-when">
              {p.whenLines.map((x) => (
                <li key={x.what}>
                  <b>{x.what}</b>
                  <small>{x.how}</small>
                </li>
              ))}
            </ul>
          ) : (
            <b>{p.when || "To be set together"}</b>
          )}
        </div>
        <div className="pp-price">
          <span>{p.once ? "Price" : "Monthly price"}</span>
          <b>{moneyExact(totalCents)}</b>
          {p.once ? null : <small>billed monthly</small>}
        </div>
      </div>
      {p.about ? (
        <section className="pp-section">
          <h2>Your building</h2>
          <p className="pp-pre">{p.about}</p>
        </section>
      ) : null}
      {p.scope.length ? (
        <section className="pp-section">
          <h2>Scope of work</h2>
          {p.scope.map((x) => (
            <div key={x.label}>
              <h3>{x.label}</h3>
              {check(x.items)}
            </div>
          ))}
        </section>
      ) : null}
      {p.bring.length ? (
        <section className="pp-section">
          <h2>What we bring</h2>
          <p className="muted">Everything needed for the job comes with us.</p>
          {check(p.bring)}
        </section>
      ) : null}
      {p.insurance ? (
        <section className="pp-section">
          <h2>Insurance</h2>
          <p>{p.insurance}</p>
        </section>
      ) : null}
      {p.highlights.length ? (
        <section className="pp-section">
          <h2>Why {brand}</h2>
          {check(p.highlights)}
        </section>
      ) : null}
      {p.quality ? (
        <section className="pp-section">
          <h2>How we keep it right</h2>
          <p>{p.quality}</p>
        </section>
      ) : null}
      {p.rating || p.reviews.length ? (
        <section className="pp-section">
          <h2>What clients say</h2>
          {p.rating ? <p className="pp-kicker">{p.rating}</p> : null}
          {p.reviews.map((r) => (
            <blockquote key={r.text} className="pp-review">
              <p>{r.text}</p>
              {r.name ? <cite>{r.name}</cite> : null}
            </blockquote>
          ))}
        </section>
      ) : null}
    </>
  );
}

/** The client's visit report from its link (draft 7b): what was done, by whom, room by room. */
export function VisitReportPage({ token }: { token: string }) {
  const r = useQuery({
    queryKey: ["public-report", token],
    queryFn: () => api<VisitReportPublic>("GET", `public/report/${token}`),
  });
  if (r.error) return <NotFound what="report" />;
  const d = r.data;
  if (!d)
    return (
      <Branded brand={null}>
        <Skeleton rows={6} />
      </Branded>
    );
  const rooms = [...new Set(d.done.map((x) => x.room ?? ""))];
  return (
    <Branded
      title={clientLinkTitle("report", { brand: d.business }).title}
      brand={{
        name: d.business,
        color: d.brandColor,
        look: d.look,
        phone: null,
        email: null,
        links: [],
        address: null,
        website: null,
      }}
    >
      <h1>
        Hi {d.firstName}, your {d.job.toLowerCase()} is done
      </h1>
      <p className="muted">
        {shortDate(d.date)}
        {d.crew.length ? `, by ${d.crew.join(" and ")}` : ""}.
      </p>
      {d.done.length ? (
        rooms.map((room) => (
          <section key={room} className="stack">
            {room ? <h2>{room}</h2> : null}
            <ul>
              {d.done
                .filter((x) => (x.room ?? "") === room)
                .map((x) => (
                  <li key={x.label}>{x.label}</li>
                ))}
            </ul>
          </section>
        ))
      ) : (
        <p>Everything on your visit is done.</p>
      )}
      {d.note ? <p>{d.note}</p> : null}
    </Branded>
  );
}

/**
 * Approving on the quote page, like the old GroundsMen quote page: name, signature, then the card when the quote needs
 * one, and one button that saves the card and approves.
 */
function ApproveBox({
  token,
  q,
  needCard,
  name,
  setName,
  setDrawing,
  err,
  busy,
  go,
  nameWords,
  goWords,
  agreeWords,
}: {
  token: string;
  q: PublicQuote;
  needCard: boolean;
  name: string;
  setName: (v: string) => void;
  setDrawing: (v: string | null) => void;
  err: string | null;
  busy: boolean;
  go: (card: CardFields | null) => void;
  nameWords: string;
  goWords: string;
  /** A box to tick before signing, when there are terms (owner, 2026-11-03). Empty for none. */
  agreeWords: string;
}) {
  const card = useCardFields(token, needCard);
  const [agreed, setAgreed] = useState(false);
  return (
    <div className="public-sign">
      <label className="public-label" htmlFor="sign">
        {nameWords}
      </label>
      <div className="input">
        <input id="sign" autoComplete="name" value={name} onChange={(e) => setName(e.target.value)} />
      </div>
      {q.policies.length ? (
        <div className="public-policies">
          <p className="public-label">By signing, you agree to these policies.</p>
          {q.policies.map((p) => (
            <section key={p.key}>
              <h3>{p.title}</h3>
              <p>{p.text}</p>
            </section>
          ))}
        </div>
      ) : null}
      {q.signRequired ? <SignaturePad onChange={setDrawing} /> : null}
      {needCard ? (
        <CardBox
          card={card}
          title="A card on file books your service. You are not charged now."
          sentence={
            <>
              {q.saveCardSentence} <CardTermsLink />
            </>
          }
        />
      ) : null}
      {agreeWords ? (
        <label className="public-agree">
          <input type="checkbox" checked={agreed} onChange={(e) => setAgreed(e.target.checked)} />
          <span>{agreeWords}</span>
        </label>
      ) : null}
      {err ? <Banner type="error">{err}</Banner> : null}
      <Button
        size="large"
        fullWidth
        loading={busy}
        disabled={Boolean(agreeWords) && !agreed}
        onClick={() => go(needCard ? card : null)}
      >
        {needCard && goWords === "Approve quote" ? "Approve and save card" : goWords}
      </Button>
    </div>
  );
}

/**
 * The client's review page: tap 1 to 5 stars. 4 or 5 go straight on to Google. 1 to 3 get a note box so the owner can
 * make it right, and no Google button (owner, Oct 30). The page names the job, the day and the crew with their pictures,
 * and shows the brand's Google rating (owner, 2026-11-02: "it seems so bland and nothing there, maybe a picture of the
 * team"). A step to tap what stood out and copy words before Google was tried and taken out the same night (owner: "i
 * dont think it would do good and adds friction").
 */
const STAR_WORDS = ["", "Not good", "Could be better", "Okay", "Great", "Loved it"];

const namesOf = (crew: { first: string }[]) =>
  crew.length <= 1
    ? (crew[0]?.first ?? "")
    : `${crew
        .slice(0, -1)
        .map((c) => c.first)
        .join(", ")} and ${crew.at(-1)!.first}`;

function CrewFaces({
  token,
  crew,
  color,
}: {
  token: string;
  crew: { id: string; first: string; photo: boolean }[];
  color: string;
}) {
  if (crew.length === 0) return null;
  return (
    <div className="rv-faces" aria-hidden="true">
      {crew.slice(0, 4).map((c) =>
        c.photo ? (
          <img key={c.id} src={`/v1/public/review/${token}/crew/${c.id}`} alt="" />
        ) : (
          <span key={c.id} style={{ background: color }}>
            {c.first.charAt(0).toUpperCase()}
          </span>
        ),
      )}
    </div>
  );
}

export function ReviewPage({ token }: { token: string }) {
  const r = useQuery({
    queryKey: ["public-review", token],
    queryFn: () => api<ReviewPagePublic>("GET", `public/review/${token}`),
  });
  const [stars, setStars] = useState(0);
  const [hover, setHover] = useState(0);
  const [note, setNote] = useState("");
  const [step, setStep] = useState<"pick" | "note" | "thanks" | "going">("pick");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  if (r.error) return <NotFound what="page" />;
  const d = r.data;
  if (!d)
    return (
      <Branded brand={null}>
        <Skeleton rows={4} />
      </Branded>
    );
  const crew = d.crew ?? [];
  const crewNames = namesOf(crew);
  const color = d.look?.accent ?? d.brandColor ?? "#1f6f5c";
  const send = async (n: number, note?: string) => {
    setBusy(true);
    setErr(null);
    try {
      const a = await api<ReviewRateAnswer>("POST", `public/review/${token}`, { stars: n, ...(note ? { note } : {}) });
      if (n >= 4 && a.go) {
        setStep("going");
        window.location.assign(a.go);
      } else setStep(n >= 4 || note !== undefined ? "thanks" : "note");
    } catch (e) {
      setErr(errText(e));
    } finally {
      setBusy(false);
    }
  };
  const pick = (n: number) => {
    setStars(n);
    void send(n);
  };
  const shown = hover || stars;
  return (
    <Branded
      title={clientLinkTitle("review", { brand: d.business, first: d.firstName }).title}
      brand={{
        name: d.business,
        color: d.brandColor,
        look: d.look,
        phone: null,
        email: null,
        links: [],
        address: null,
        website: null,
      }}
    >
      <div className="rv">
        {step === "going" ? (
          <>
            <CrewFaces token={token} crew={crew} color={color} />
            <h1>Thank you, {d.firstName}!</h1>
            <p className="rv-lead">Opening Google so you can share it there.</p>
          </>
        ) : step === "thanks" ? (
          <>
            <h1>Thank you, {d.firstName}</h1>
            <p>We read every note. Someone from {d.business} will reach out soon.</p>
          </>
        ) : step === "note" ? (
          <>
            <h1>We are sorry, {d.firstName}</h1>
            <p>Tell us what went wrong so we can make it right.</p>
            <textarea
              rows={5}
              maxLength={2000}
              value={note}
              aria-label="What went wrong"
              onChange={(e) => setNote(e.target.value)}
            />
            {err ? <Banner type="error">{err}</Banner> : null}
            <Button size="large" fullWidth loading={busy} onClick={() => void send(stars, note.trim())}>
              Send
            </Button>
          </>
        ) : (
          <>
            <CrewFaces token={token} crew={crew} color={color} />
            {d.job ? (
              <p className="rv-eyebrow">
                Your {d.job}
                {d.day ? ` on ${shortDate(d.day)}` : ""}
              </p>
            ) : null}
            <h1>How did we do, {d.firstName}?</h1>
            <p className="rv-lead">
              {crewNames ? `${crewNames} would love to hear from you.` : "We would love to hear from you."} It takes 20 seconds.
            </p>
            <div className="review-stars" role="radiogroup" aria-label="Your rating" onMouseLeave={() => setHover(0)}>
              {[1, 2, 3, 4, 5].map((n) => (
                <button
                  key={n}
                  type="button"
                  role="radio"
                  aria-checked={stars === n}
                  aria-label={`${String(n)} star${n === 1 ? "" : "s"}, ${STAR_WORDS[n]!}`}
                  className={n <= shown ? "on" : ""}
                  disabled={busy}
                  onMouseEnter={() => setHover(n)}
                  onClick={() => pick(n)}
                >
                  <svg viewBox="0 0 24 24" aria-hidden="true">
                    <path d="M12 2.5l2.9 6.1 6.6.8-4.9 4.6 1.3 6.6L12 17.3 6.1 20.6l1.3-6.6-4.9-4.6 6.6-.8z" />
                  </svg>
                </button>
              ))}
            </div>
            <p className="rv-word" aria-live="polite">
              {shown ? STAR_WORDS[shown] : "Tap a star"}
            </p>
            {err ? <Banner type="error">{err}</Banner> : null}
            {d.google ? (
              <p className="rv-proof">
                <span aria-hidden="true">★</span> {d.google.rating.toFixed(1)} on Google from {d.google.reviews.toLocaleString()}{" "}
                neighbors
              </p>
            ) : null}
          </>
        )}
      </div>
    </Branded>
  );
}

/** What a proposal's line item includes, as a short list under the line. */
function Included({ items }: { items: readonly string[] }) {
  if (!items.length) return null;
  return (
    <span className="public-included">
      <span className="small">What's included</span>
      <ul>
        {items.map((x) => (
          <li key={x}>{x}</li>
        ))}
      </ul>
    </span>
  );
}

/** A line's page that lists what is included, from the price list. Opens in a new tab so the quote stays open. */
function SeeIncluded({ link }: { link: string | null }) {
  if (!link) return null;
  return (
    <a className="see-included small" href={link} target="_blank" rel="noreferrer">
      See what's included
    </a>
  );
}
